import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { fetchFastApi } from "../api/fastapi";
import { toast } from "sonner";
import { FileType } from "../models/audit/auditSession.ts";
import {
  pdfDocument,
  videoDocument,
  VideoDocument,
} from "../models/extraction.ts";
import { z } from "zod";
import { isVideoFileExtension } from "../lib/fileTypes.ts";
import {
  resolveDocumentExtraction,
  type PdfDocumentExtraction,
} from "../lib/documentExtraction.ts";
import { useNavigate, useParams } from "react-router-dom";
import {
  ControlSessionEnvelope,
  TestPhase,
} from "../models/audit/controlSession.ts";
import {
  DocumentProcessingStatus,
  DocumentProcessingStatusSchema,
} from "../models/audit/documentProcessingStatus.ts";
import {
  SessionKind,
  useControlSessionEnvelope,
} from "../hooks/audit/useControlSessionEnvelope";
import { useAuditMutations } from "../hooks/audit/useAuditMutations";

interface SelectedDocument {
  id?: string;
  documentId?: string;
  type?: string;
  fetchableDocument?: {
    fileType?: FileType;
    fileName?: string;
  };
  fileType?: FileType;
  fileName?: string;
  isMissing?: boolean;
  /**
   * ID of the procedure session that owns this document. Required when the
   * document is non-null so `updateCurrentDocument` can resolve a presigned
   * URL without walking the cache.
   */
  procedureSessionId?: string;
}

/**
 * Phase 3: `CurrentSelection` moved off the workpaper view's module scope so
 * the thin shell (`AuditWorkPaperViewV2`) and its per-procedure child
 * (`ProcedureSessionWorkPaper`) can share selection state without
 * prop-drilling. `procedureSessionId` is new in Phase 3 — it drives the
 * `<ProcedureSessionWorkPaper key={procedureSessionId} />` remount so
 * view-local state auto-resets when the user switches procedures.
 */
export interface CurrentSelection {
  procedureSessionId?: string;
  checkResultId?: string;
  sourceRowId?: string;
  documentId?: string;
  attributeId?: string;
  supportColumnId?: string;
  evalColumnId?: string;
  value?: string;
}

interface AuditContextType {
  currentSelectedDocumentUrl: {
    url: string;
    documentExtraction?: z.infer<typeof pdfDocument>;
    videoDocument?: VideoDocument;
    videoDocumentError?: boolean;
  } | null;
  setCurrentSelectedDocumentUrl: React.Dispatch<
    React.SetStateAction<{
      url: string;
      documentExtraction?: z.infer<typeof pdfDocument>;
      videoDocument?: VideoDocument;
      videoDocumentError?: boolean;
    } | null>
  >;
  controlSession: ControlSessionEnvelope | null;
  selectedDocument: SelectedDocument | null;
  setSelectedDocument: React.Dispatch<
    React.SetStateAction<SelectedDocument | null>
  >;
  uploadDocumentsToAuditSession: ReturnType<
    typeof useAuditMutations
  >["uploadDocumentsToAuditSession"];
  uploadDocumentsToControlSessionProcedures: ReturnType<
    typeof useAuditMutations
  >["uploadDocumentsToControlSessionProcedures"];
  uploadSampleListToAuditSession: ReturnType<
    typeof useAuditMutations
  >["uploadSampleListToAuditSession"];
  createSourceRow: ReturnType<typeof useAuditMutations>["createSourceRow"];
  createSamples: ReturnType<typeof useAuditMutations>["createSamples"];
  uploadGuidanceDocumentsToControlSessionProcedures: ReturnType<
    typeof useAuditMutations
  >["uploadGuidanceDocumentsToControlSessionProcedures"];
  updateCurrentDocument: (document: SelectedDocument | null) => Promise<void>;
  updateDocumentCategory: ReturnType<
    typeof useAuditMutations
  >["updateDocumentCategory"];
  fetchPresignedUrl: (
    file_id: string,
    procedureSessionId: string,
  ) => Promise<{
    url: string;
    expiresAt: number;
    documentExtraction?: z.infer<typeof pdfDocument>;
  } | null>;
  fetchDocumentProcessingStatus: (
    controlSessionId: string,
  ) => Promise<DocumentProcessingStatus | null>;
  documentUploadNonce: number;
  notifyDocumentUploadStarted: () => void;
  updateControlSessionName: (name: string) => Promise<void>;
  updateControlSessionPhase: (testPhase: TestPhase) => Promise<void>;
  updateControlSessionFyCycle: (fyCycleId: string | null) => Promise<void>;
  submitForReview: () => Promise<void>;
  reopenSession: () => Promise<void>;
  controlSessionLoading: boolean;
  currentSelection: CurrentSelection | undefined;
  setCurrentSelection: React.Dispatch<
    React.SetStateAction<CurrentSelection | undefined>
  >;
  /**
   * Which entity heads this session shell — `"control"` for the classic
   * control-session routes, `"work_step"` for the work-step sibling routes.
   * Shared pages use it to point the status hook at the right endpoint.
   */
  sessionKind: SessionKind;
}

const AuditContext = createContext<AuditContextType | undefined>(undefined);

export function AuditProvider({
  children,
  sessionKind = "control",
}: {
  children: ReactNode;
  sessionKind?: SessionKind;
}) {
  const navigate = useNavigate();
  const { controlSessionId } = useParams();

  // Phase 2: the envelope hook owns control-session-level fetching. Per-slice
  // hooks (`useProcedureSession`, `useProcedureSessionTableShell`,
  // `useProcedureSessionDocuments`, `useSourceRow`) handle the per-procedure
  // heavy data. The same envelope shape is served for work step sessions
  // from `/work-steps` — `sessionKind` picks the endpoint.
  const {
    data: controlSession,
    isLoading: controlSessionLoading,
    error: controlSessionError,
  } = useControlSessionEnvelope(controlSessionId, sessionKind);

  /*
   * Redirect to the owning list page if the envelope fetch fails — preserves
   * the old `fetchControlSession` error-handling contract. Gated on the
   * absence of data for the same reason AuditSessionPageV2 gates its spinner
   * that way: every mutation that invalidates the envelope produces a
   * background refetch which keeps its cached data, so without `!controlSession`
   * one flaky refetch ejects an auditor mid-session and tells them a session
   * they are looking at does not exist. A failed background refetch already
   * surfaces through hookCache's own toast.
   */
  useEffect(() => {
    if (controlSessionError && !controlSession) {
      console.error("Error fetching session envelope:", controlSessionError);
      if (sessionKind === "work_step") {
        toast.error("Test not found. Redirecting to Work Steps.");
        navigate("/audit/work-steps", { replace: true });
      } else {
        toast.error("Control session not found. Redirecting to Controls.");
        navigate("/audit/controls/v2", { replace: true });
      }
    }
  }, [controlSessionError, controlSession, navigate, sessionKind]);

  const [selectedDocument, setSelectedDocument] =
    useState<SelectedDocument | null>(null);
  const [currentSelectedDocumentUrl, setCurrentSelectedDocumentUrl] = useState<{
    url: string;
    documentExtraction?: z.infer<typeof pdfDocument>;
    videoDocument?: VideoDocument;
    videoDocumentError?: boolean;
  } | null>(null);
  // Monotonic token for in-flight document fetches. Each `updateCurrentDocument`
  // bumps it; a slow/out-of-order fetch only commits its URL if it's still the
  // latest request — otherwise an earlier doc resolving after a newer selection
  // would render the wrong document under the new filename + callouts.
  const docRequestSeq = useRef(0);
  const [presignedUrlsMap, setPresignedUrlsMap] = useState<
    Record<
      string,
      {
        url: string;
        documentExtraction?: z.infer<typeof pdfDocument>;
        expiresAt: number;
      }
    >
  >({});

  const [documentUploadNonce, setDocumentUploadNonce] = useState<number>(0);

  const [currentSelection, setCurrentSelection] = useState<
    CurrentSelection | undefined
  >(undefined);

  const notifyDocumentUploadStarted = useCallback(() => {
    setDocumentUploadNonce((n) => n + 1);
  }, []);

  const mutations = useAuditMutations({
    controlSession: controlSession ?? null,
    notifyDocumentUploadStarted,
    sessionKind,
  });

  const fetchDocumentProcessingStatus = useCallback(
    async (controlSessionId: string) => {
      const path =
        sessionKind === "work_step"
          ? `/work-steps/work-step-session/${controlSessionId}/document-processing-status`
          : `/controls/v2/control-session/${controlSessionId}/document-processing-status`;
      try {
        const response = await fetchFastApi({
          path,
          method: "GET",
        });
        return DocumentProcessingStatusSchema.parse(response);
      } catch (error) {
        console.error("Error fetching document processing status:", error);
        return null;
      }
    },
    [sessionKind],
  );

  const fetchPresignedUrl = useCallback(
    async (file_id: string, procedureSessionId: string) => {
      try {
        if (presignedUrlsMap[file_id]) {
          return presignedUrlsMap[file_id];
        }

        const response = await fetchFastApi({
          path: `/matching/session/${procedureSessionId}/session-document/${file_id}`,
          method: "GET",
        });
        const url = response.documentUrlInfo.url;

        // Extract expiration timestamp from URL
        const expiresParam = new URL(url).searchParams.get("Expires");
        const expiresAt = expiresParam ? parseInt(expiresParam) * 1000 : 0;

        let documentExtraction: PdfDocumentExtraction | undefined;
        let extractionFailed = false;
        // Callers gate the viewer on this promise, so the extraction resolves
        // before the PDF renders. Under the presigned shape that adds one round
        // trip to the render path — the session-document response, then a GET of
        // the presentation copy — and removes the per-request server-side rebuild
        // of the same payload that the inline shape required. On large documents
        // the removed rebuild outweighs the added round trip.
        if (response.extractionUrlInfo) {
          try {
            documentExtraction = await resolveDocumentExtraction(
              response.extractionUrlInfo,
            );
          } catch (error) {
            // The PDF itself is still viewable without its extraction, so a
            // failed or malformed elements payload degrades to no highlights
            // rather than no document. Deliberately not cached: the failure is
            // usually transient, and the cache above has no expiry, so caching
            // it would make one blip permanent for the session.
            extractionFailed = true;
            console.error("Error resolving document extraction:", error);
          }
        }

        if (!extractionFailed) {
          setPresignedUrlsMap((prev) => ({
            ...prev,
            [file_id]: { url, expiresAt, documentExtraction },
          }));
        }
        return { url, expiresAt, documentExtraction };
      } catch (error) {
        console.error("Error fetching presigned URL:", error);
        toast.error("Error fetching presigned URL");
        return null;
      }
    },
    [presignedUrlsMap],
  );

  const fetchVideoDocumentContent = useCallback(
    async (
      documentId: string,
      controlSessionId?: string,
    ): Promise<{ data?: VideoDocument; error?: boolean }> => {
      try {
        const queryParams = controlSessionId
          ? `?control_session_id=${controlSessionId}`
          : "";
        const response = await fetchFastApi({
          path: `/matching/document/${documentId}/content${queryParams}`,
          method: "GET",
        });
        return { data: videoDocument.parse(response) };
      } catch (error) {
        console.error("Error fetching video document content:", error);
        return { error: true };
      }
    },
    [],
  );

  const updateCurrentDocument = useCallback(
    async (document: SelectedDocument | null) => {
      const requestId = ++docRequestSeq.current;
      const isStale = () => requestId !== docRequestSeq.current;
      setCurrentSelectedDocumentUrl(null);
      setSelectedDocument(document);
      if (!document || !document.id) return;
      if (!document.procedureSessionId) {
        console.error(
          "updateCurrentDocument: missing procedureSessionId on document",
          document,
        );
        setCurrentSelectedDocumentUrl(null);
        return;
      }
      const response = await fetchPresignedUrl(
        document.id,
        document.procedureSessionId,
      );
      // A newer selection started while this fetch was in flight — drop this
      // result so it can't render an old document under the new selection.
      if (isStale()) return;
      if (!response) {
        setCurrentSelectedDocumentUrl(null);
        return;
      }
      const { url, documentExtraction } = response;

      // Fetch video content if file type is video
      const fileType =
        document.fetchableDocument?.fileType || document.fileType;
      let videoDoc: VideoDocument | undefined;
      let videoDocError: boolean | undefined;
      if (fileType && isVideoFileExtension(fileType)) {
        const result = await fetchVideoDocumentContent(
          document.documentId ?? document.id,
          controlSession?.id,
        );
        videoDoc = result.data;
        videoDocError = result.error;
      }

      // Re-check after the (possible) video fetch for the same reason.
      if (isStale()) return;
      setCurrentSelectedDocumentUrl({
        url,
        documentExtraction,
        videoDocument: videoDoc,
        videoDocumentError: videoDocError,
      });
    },
    [fetchPresignedUrl, fetchVideoDocumentContent, controlSession?.id],
  );

  return (
    <AuditContext.Provider
      value={{
        selectedDocument,
        setSelectedDocument,
        ...mutations,
        fetchPresignedUrl,
        currentSelectedDocumentUrl,
        setCurrentSelectedDocumentUrl,
        updateCurrentDocument,
        fetchDocumentProcessingStatus,
        documentUploadNonce,
        notifyDocumentUploadStarted,
        controlSessionLoading,
        controlSession: controlSession ?? null,
        currentSelection,
        setCurrentSelection,
        sessionKind,
      }}
    >
      {children}
    </AuditContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAudit() {
  const context = useContext(AuditContext);
  if (context === undefined) {
    throw new Error("useAudit must be used within an AuditProvider");
  }
  return context;
}
